import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatPercent, formatRoas, formatDecimal } from "@/lib/formatters";
import { AdPreview } from "../AdPreview";
import type { TopAdRow } from "./types";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function TopAdsTable({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey, sourceType } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery<TopAdRow[]>({
    queryKey: ["dashboard-top-ads", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate, metric: "roas", limit: "10" });
      const res = await fetch(getUrl("top-ads", params));
      if (!res.ok) throw new Error("Failed to fetch top ads");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Top Ads" widgetType="table" isEditMode={isEditMode} isLoading={isLoading} error={error instanceof Error ? error.message : null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-top-ads">No ad data available for this period</div>
      ) : (
        <div className="overflow-auto h-full" data-testid="table-top-ads">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white border-b border-gray-100">
              <tr>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Ad</th>
                {sourceType !== "unified" && <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Format</th>}
                {sourceType === "unified" && <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Account</th>}
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Spend</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">ROAS</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Purch.</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">CTR</th>
                <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">CPC</th>
                {sourceType !== "unified" && <th className="text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider py-2 px-3">Status</th>}
              </tr>
            </thead>
            <tbody>
              {(data || []).map((ad) => {
                return (
                  <tr key={ad.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="py-2 px-3">
                      <AdPreview ad={ad} />
                    </td>
                    {sourceType !== "unified" && (
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                          ad.format === "video" ? "bg-purple-50 text-purple-700" :
                          ad.format === "carousel" ? "bg-blue-50 text-blue-700" :
                          "bg-gray-100 text-gray-600"
                        }`}>
                          {ad.format || "—"}
                        </span>
                      </td>
                    )}
                    {sourceType === "unified" && (
                      <td className="py-2 px-3 text-gray-600 text-[11px] truncate max-w-[140px]">{ad.accountName || "—"}</td>
                    )}
                    <td className="py-2 px-3 text-gray-700">{formatCurrency(ad.metrics?.spend)}</td>
                    <td className="py-2 px-3 font-medium text-gray-900">{formatRoas(ad.metrics?.roas)}</td>
                    <td className="py-2 px-3 text-gray-700">{formatNumber(ad.metrics?.purchases)}</td>
                    <td className="py-2 px-3 text-gray-700">{formatPercent(ad.metrics?.ctr)}</td>
                    <td className="py-2 px-3 text-gray-700">{formatCurrency(ad.metrics?.cpc)}</td>
                    {sourceType !== "unified" && (
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ad.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {ad.status || "—"}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardWidget>
  );
}
